import classNames from "classnames";
import noop from "lodash/noop";
import upperFirst from "lodash/upperFirst";
import { FunctionComponent } from "react";
import { useIntl } from "react-intl";
import CheckoutCreditCardPaymentForm from "@Components/molecules/cart/CheckoutCreditCardPaymentForm";
import CheckoutStep from "@Components/molecules/cart/CheckoutStep";
import RadioCard from "@Components/molecules/RadioCard";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import { ReactComponent as IconShield } from "@Icons/colored/shield.svg";
import { PaymentMethod } from "@Types/cart";
import { FormError } from "@Types/error";
import { getPaymentMethodLogos } from "@Utils/cart/payment";
import { getFormErrorMessages } from "@Utils/error";
import "./style.scss";

interface Props {
  availablePaymentMethods: PaymentMethod[];
  disabled: boolean;
  editable: boolean;
  enableRememberCreditCard: boolean;
  hasCheckedRememberCreditCard: boolean;
  onChangePaymentMethod: (method: PaymentMethod) => void;
  onEdit: () => void;
  onRememberCreditCard: () => void;
  onValidate: () => void;
  paymentErrors: FormError[];
  removePaymentErrors: () => void;
  savedCreditCardIdentifier: string;
  selectedPaymentMethod: PaymentMethod | undefined;
  validatedPaymentMethod: boolean;
}

const CheckoutPaymentStep: FunctionComponent<Props> = ({
  availablePaymentMethods,
  disabled,
  editable,
  enableRememberCreditCard,
  hasCheckedRememberCreditCard,
  onChangePaymentMethod,
  onEdit,
  onRememberCreditCard,
  onValidate,
  paymentErrors,
  removePaymentErrors,
  savedCreditCardIdentifier,
  selectedPaymentMethod,
  validatedPaymentMethod,
}) => {
  const { isSmallScreen } = useBreakpoint();
  const intl = useIntl();

  const handleClick = (paymentMethod: PaymentMethod) => {
    paymentErrors.length > 0 && !isSmallScreen && removePaymentErrors();
    onChangePaymentMethod(paymentMethod);
  };

  return (
    <div className="CheckoutPaymentStep">
      <CheckoutStep
        buttonText={intl.formatMessage({
          id: "cheerz_CheckoutPaymentBottomSheet_button__ccPayment",
        })}
        disabled={disabled || !selectedPaymentMethod || paymentErrors.length > 0}
        displayButton={isSmallScreen ? false : true}
        editable={editable}
        icon={<IconShield />}
        onEdit={onEdit}
        onValidate={selectedPaymentMethod ? onValidate : noop}
        title={intl.formatMessage({ id: "cheerz_CheckoutPaymentStep_title" })}
        validated={validatedPaymentMethod}
      >
        <div
          className={classNames("CheckoutPaymentStep-paymentMethods", {
            "CheckoutPaymentStep-paymentMethods--editablePaymentMethods": editable,
          })}
        >
          <div className="CheckoutPaymentStep-summary">
            <div className="CheckoutPaymentStep-summary-title">
              {intl.formatMessage({
                id: "cheerz_CheckoutPaymentStep_summary_paymentMethods_title",
              })}
            </div>
            <div className="CheckoutPaymentStep-summary-description">
              {selectedPaymentMethod === "cc"
                ? `${intl.formatMessage({
                    id: "cheerz_CheckoutPaymentStep_ccPayment_title",
                  })} - **** ${savedCreditCardIdentifier}`
                : upperFirst(selectedPaymentMethod)}
            </div>
          </div>

          <div className="CheckoutPaymentStep-content">
            {intl.formatMessage({ id: "cheerz_CheckoutPaymentStep_description" })}
            {new Set(availablePaymentMethods).has("cc") && (
              <RadioCard
                checked={selectedPaymentMethod === "cc"}
                disabled={disabled}
                errorMessage={
                  selectedPaymentMethod === "cc" && paymentErrors.length > 0
                    ? getFormErrorMessages(paymentErrors, isSmallScreen ? true : false)
                    : undefined
                }
                hasError={selectedPaymentMethod === "cc" && paymentErrors.length > 0}
                onClick={() => handleClick("cc")}
                title={intl.formatMessage({ id: "cheerz_CheckoutPaymentStep_ccPayment_title" })}
              >
                <div className="CheckoutPaymentStep-cc-RadioCard-content">
                  <div className="CheckoutPaymentStep-cc-RadioCard-content-description">
                    {selectedPaymentMethod === "cc" && isSmallScreen
                      ? `${intl.formatMessage({
                          id: "cheerz_CheckoutPaymentStep_ccPayment_title",
                        })} - **** ${savedCreditCardIdentifier}`
                      : intl.formatMessage({
                          id: "cheerz_CheckoutPaymentStep_ccPayment_description",
                        })}
                  </div>
                  {!isSmallScreen && selectedPaymentMethod === "cc" && (
                    <CheckoutCreditCardPaymentForm
                      disabled={disabled}
                      enableRememberCreditCard={enableRememberCreditCard}
                      hasCheckedRememberCreditCard={hasCheckedRememberCreditCard}
                      onRememberCreditCard={onRememberCreditCard}
                      paymentErrors={paymentErrors}
                    />
                  )}
                </div>
              </RadioCard>
            )}
            {availablePaymentMethods.map((paymentMethod) => {
              return (
                paymentMethod !== "cc" &&
                paymentMethod !== "free" && (
                  <RadioCard
                    checked={selectedPaymentMethod === paymentMethod}
                    disabled={disabled}
                    errorMessage={
                      selectedPaymentMethod === paymentMethod && paymentErrors.length > 0
                        ? getFormErrorMessages(paymentErrors, false)
                        : undefined
                    }
                    hasError={selectedPaymentMethod === paymentMethod && paymentErrors.length > 0}
                    key={paymentMethod}
                    logo={!isSmallScreen ? getPaymentMethodLogos(paymentMethod)[0] : undefined}
                    onClick={() => handleClick(paymentMethod)}
                    title={upperFirst(paymentMethod)}
                  >
                    <div className="CheckoutPaymentStep-alternatePayment-RadioCard-content-description">
                      {intl.formatMessage(
                        {
                          id: !isSmallScreen
                            ? "cheerz_CheckoutPaymentStep_alternatePayment_description__desktop"
                            : selectedPaymentMethod === paymentMethod && paymentErrors.length === 0
                            ? "cheerz_CheckoutPaymentStep_alternatePayment_description_selected__mobile"
                            : "cheerz_CheckoutPaymentStep_alternatePayment_description__mobile",
                        },
                        {
                          paymentMethod: upperFirst(paymentMethod),
                        }
                      )}
                    </div>
                  </RadioCard>
                )
              );
            })}
          </div>
        </div>
      </CheckoutStep>
    </div>
  );
};

export default CheckoutPaymentStep;
